'use client';
import { useState } from 'react';
import { motion } from 'motion/react';
import { Heart, Pencil } from 'lucide-react';
import { categories, type Category } from '@curio/contracts';
import { event } from '@curio/client';
import { CategoryIcon } from '../components/category-icons';
import { InterestsDialog, useInterests } from './interests';
/** Per-page selection. "For you" follows the shared interests; explicit picks are local to the page. */
export interface CategoryFilter {
  forYou: boolean;
  selected: Category[];
}
export const noFilter: CategoryFilter = { forYou: false, selected: [] };
export const forYouFilter: CategoryFilter = { forYou: true, selected: [] };
export function effectiveCategories(
  filter: CategoryFilter,
  interests: readonly Category[],
): Category[] {
  return filter.forYou ? [...interests] : filter.selected;
}
export function toggleCategory(
  filter: CategoryFilter,
  interests: readonly Category[],
  category: Category,
): CategoryFilter {
  const base = effectiveCategories(filter, interests);
  return {
    forYou: false,
    selected: base.includes(category)
      ? base.filter((c) => c !== category)
      : [...base, category],
  };
}
export const filterKey = (cats: readonly string[]) => cats.join(',');
const iconVariants = {
  on: { scale: [1, 1.3, 1], rotate: [0, -12, 0] },
  off: { scale: 1, rotate: 0 },
};
export function CategoryRow({
  filter,
  onChange,
  label = 'Categories',
  personal = true,
}: {
  filter: CategoryFilter;
  onChange: (next: CategoryFilter) => void;
  label?: string;
  /** Show the "For you" chip. The categories page browses everything and leaves it out. */
  personal?: boolean;
}) {
  const { interests, setInterests } = useInterests();
  const [editing, setEditing] = useState(false);
  const active = effectiveCategories(filter, interests);
  const report = (value: string) =>
    event('catalog_filter_changed', undefined, { filter: 'category', value });
  return (
    <div className="category-row" role="group" aria-label={label}>
      {personal && (
        <motion.button
          type="button"
          className={`chip category-chip chip-for-you ${filter.forYou ? 'active' : ''}`}
          aria-pressed={filter.forYou}
          whileTap={{ scale: 0.94 }}
          onClick={() => {
            if (!interests.length) {
              setEditing(true);
              return;
            }
            const next = filter.forYou ? noFilter : forYouFilter;
            onChange(next);
            report(next.forYou ? 'for_you' : 'all');
          }}
        >
          <motion.span
            className="category-chip-icon"
            variants={iconVariants}
            animate={filter.forYou ? 'on' : 'off'}
            transition={{ duration: 0.35 }}
          >
            <Heart
              size={15}
              aria-hidden="true"
              fill={filter.forYou ? 'currentColor' : 'none'}
            />
          </motion.span>
          For you
        </motion.button>
      )}
      {personal && filter.forYou && (
        <motion.button
          type="button"
          className="chip category-chip category-chip-edit"
          aria-label="Edit interests"
          initial={{ opacity: 0, width: 0, paddingInline: 0 }}
          animate={{ opacity: 1, width: 'auto', paddingInline: 12 }}
          transition={{ duration: 0.22 }}
          onClick={() => setEditing(true)}
        >
          <Pencil size={14} aria-hidden="true" />
        </motion.button>
      )}
      {categories.map((c) => {
        const on = active.includes(c);
        return (
          <motion.button
            key={c}
            type="button"
            className={`chip category-chip ${on ? 'active' : ''}`}
            aria-pressed={on}
            whileTap={{ scale: 0.94 }}
            onClick={() => {
              const next = toggleCategory(filter, interests, c);
              onChange(next);
              report(next.selected.length ? c : 'all');
            }}
          >
            <motion.span
              className="category-chip-icon"
              variants={iconVariants}
              animate={on ? 'on' : 'off'}
              transition={{ duration: 0.35 }}
            >
              <CategoryIcon category={c} size={15} />
            </motion.span>
            {c}
          </motion.button>
        );
      })}
      <InterestsDialog
        open={editing}
        initial={interests}
        onClose={() => setEditing(false)}
        onSave={async (next) => {
          setEditing(false);
          await setInterests(next);
          if (next.length && !filter.forYou && !filter.selected.length)
            onChange(forYouFilter);
        }}
      />
    </div>
  );
}
